import { ICellRendererParams } from "ag-grid-community";

import type { RequestRow } from "../requestLogGridTypes";

import { HydrationPendingCell, isRequestRowHydrating } from "./HydrationPendingCell";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { Template } from "@/types";
import { useTruncatedList } from "@/hooks/useTruncatedList";
import { getTemplateFormat } from "@/utils/utils";

export const InputVariablesCellRenderer = ({ data }: ICellRendererParams<RequestRow>) => {
  const isHydrating = isRequestRowHydrating(data);
  const preview = data?.prompt_input_variables_preview;
  const keys = Object.keys(preview ?? {});
  const templateFormat = getTemplateFormat(data?.prompt_version?.prompt_template as Template);
  const { containerRef, measureRef, visibleCount, hiddenCount } = useTruncatedList({
    itemCount: keys.length,
    keepMinOne: true,
  });

  if (isHydrating && keys.length === 0) {
    return <HydrationPendingCell />;
  }

  if (keys.length === 0) {
    return <span className="text-gray-400">—</span>;
  }

  const tooltipContent = hiddenCount > 0 ? keys?.slice(visibleCount).join(", ") : undefined;

  return (
    <div
      ref={containerRef}
      className="x-36 relative flex min-w-0 flex-nowrap items-center gap-1 overflow-hidden md:w-[200px] 2xl:w-[300px]"
    >
      <div
        ref={measureRef}
        className="pointer-events-none invisible absolute flex w-full flex-nowrap items-center gap-1"
        aria-hidden="true"
      >
        {keys.map(k => (
          <span key={k} className="input-variable whitespace-nowrap">
            {templateFormat === "jinja2" ? `{{ ${k} }}` : `{${k}}`}
          </span>
        ))}
      </div>
      <div className="flex min-w-0 flex-nowrap items-center gap-1 overflow-hidden px-2 py-1 text-xs">
        {keys.slice(0, visibleCount).map(k => (
          <span key={k} className="input-variable whitespace-nowrap">
            {templateFormat === "jinja2" ? `{{ ${k} }}` : `{${k}}`}
          </span>
        ))}
        {hiddenCount > 0 && (
          <BasicTooltip content={tooltipContent}>
            <span className="input-variable whitespace-nowrap">+{hiddenCount}</span>
          </BasicTooltip>
        )}
      </div>
    </div>
  );
};
